import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Localization from '../localization';
import { AltNavBar } from '../components/NavBar';
import { getVictoriaAge, processParagraphs } from '../utils/utils';
import { TbLayoutNavbarCollapseFilled, TbLayoutBottombarCollapseFilled } from 'react-icons/tb';

import conf from '../config/web-site.json';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/About.scss';

type Shows = {
  [key: string]: {
    location: string;
    date: string;
  };
}

const strings = Localization.getStrings();

const filterSorShows = (shows: Shows) => {
  return Object.entries(shows).filter(show =>
    show[1].location === 'SOR'
  ).length;
}

type AboutProps = {
  scroller: (arg: string) => void;
  expanded: boolean;
  setExpanded: (arg: boolean) => void;
  config?: typeof conf;
}

const About: React.FC<AboutProps> = (props) => {
  const config = props.config || conf;
  const { scroller,  expanded, setExpanded} = props;

  const shows = filterSorShows(config.shows);
  const totalShows = Object.keys(config.shows).length;
  const age = getVictoriaAge();
  const yrs = new Date().getFullYear() - 2018;

  const [, setWidth] = useState(window.innerWidth);
  const [animated, setAnimated] = useState(false);
  const [counts, setCounts] = useState({ age: 0, shows: 0, yrs: 0 });
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    /* istanbul ignore next */
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!animated) return;
    const targets = { age, shows: totalShows, yrs };
    const steps = 40;
    const intervalMs = 1200 / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const ease = 1 - Math.pow(1 - Math.min(step / steps, 1), 3);
      setCounts({
        age: Math.round(targets.age * ease),
        shows: Math.round(targets.shows * ease),
        yrs: Math.round(targets.yrs * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [animated, age, totalShows, yrs]);

  const readMoreHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setExpanded(!expanded);
    if(expanded) props.scroller('about');
  }

  const aboutText = strings.t_about
    .replace('{age}', Localization.translateNumber(age))
    .replace('{shows}', Localization.translateNumber(shows));

  const allParagraphs = processParagraphs(aboutText);
  const cleanParas = allParagraphs.filter(p => (p.props.children as string)?.trim?.() !== '');
  const pullQuotePara = cleanParas[cleanParas.length - 1];
  const bodyParas = cleanParas.slice(0, -1);

  const statItems = [
    { label: strings.b_stat_age,    value: counts.age },
    { label: strings.b_stat_shows,  value: counts.shows },
    { label: strings.b_stat_yrs,    value: counts.yrs },
  ];

  return (
    <section id="about" ref={sectionRef} className={animated ? 'about-animated' : ''}>
    <AltNavBar {...{ scroller, source: config.pages.about.title }} />
      <Container>
        <Row>
            <Col data-testid='about-view'>
                <h1>{strings.t_title}</h1>
                <div className='about-stats'>
                  {statItems.map(s => (
                    <div key={s.label} className='about-stat'>
                      <span className='about-stat-value'>{s.value}</span>
                      <span className='about-stat-label'>{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className='about-text'>
                { expanded ? bodyParas : bodyParas.slice(0, 4) }
                { expanded && pullQuotePara && (
                  <blockquote className='about-pull-quote'>
                    {pullQuotePara.props.children}
                  </blockquote>
                )}
                <a href="#" onClick={readMoreHandler} className='read-more-btn'>
                  { expanded ? (
                    <>
                      {`${strings.b_read_less} `}
                      <TbLayoutNavbarCollapseFilled style={{ verticalAlign: 'text-top' }} />
                    </>
                  ) : (
                    <>
                      {`${strings.b_read_more} `}
                      <TbLayoutBottombarCollapseFilled style={{ verticalAlign: 'text-top' }} />
                    </>
                  )}
                </a>
                </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default About;
